import { API_BASE_URL } from '../config';
import { Product } from '../data/products';

function mapProduct(apiProduct: any): Product {
	return {
		id: apiProduct._id || apiProduct.id,
		title: apiProduct.title,
		description: apiProduct.description,
		shortDescription: apiProduct.shortDescription || apiProduct.description,
		image: apiProduct.image,
		price: apiProduct.price,
		originalPrice: apiProduct.originalPrice,
		category: apiProduct.category,
		amazonLink: apiProduct.amazonLink || '',
		flipkartLink: apiProduct.flipkartLink || '',
		specifications: apiProduct.specifications || {},
		features: apiProduct.features || [],
		highlights: apiProduct.highlights || [],
		videoUrl: apiProduct.videoUrl,
		slug: apiProduct.slug,
		rating: apiProduct.rating,
		reviews: apiProduct.reviews,
		featured: apiProduct.featured || false,
		sellOnSite: apiProduct.sellOnSite !== undefined ? apiProduct.sellOnSite : true,
		colorVariants: apiProduct.colorVariants || [],
	};
}

export interface ListProductsParams {
	search?: string;
	category?: string;
	minPrice?: number;
	maxPrice?: number;
	featured?: boolean;
	sort?: 'price' | '-price' | 'rating' | 'name';
	page?: number;
	limit?: number;
}

export async function listProducts(params: ListProductsParams = {}): Promise<{ items: Product[]; total: number; page: number; pages: number }>{
	try {
		// Use relative path if not on localhost (works for both mobile and server)
		let fullUrl: string;
		if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
			// Use absolute URL for localhost development
			const url = new URL(`${API_BASE_URL}/api/products`);
			Object.entries(params).forEach(([k, v]) => {
				if (v !== undefined && v !== '') {
					// Handle boolean values correctly
					if (typeof v === 'boolean') {
						url.searchParams.set(k, String(v));
					} else {
						url.searchParams.set(k, String(v));
					}
				}
			});
			fullUrl = url.toString();
		} else {
			// Use relative path for production/server/mobile
			const url = new URL('/api/products', window.location.origin);
			Object.entries(params).forEach(([k, v]) => {
				if (v !== undefined && v !== '') {
					// Handle boolean values correctly
					if (typeof v === 'boolean') {
						url.searchParams.set(k, String(v));
					} else {
						url.searchParams.set(k, String(v));
					}
				}
			});
			fullUrl = url.toString();
		}
		
		const res = await fetch(fullUrl);
		if (!res.ok) {
			console.error(`API Error: ${res.status} ${res.statusText} for URL: ${fullUrl}`);
			throw new Error(`Failed to load products: ${res.status}`);
		}
		
		const data = await res.json();
		return {
			items: (data.items || []).map(mapProduct),
			total: data.total || 0,
			page: data.page || 1,
			pages: data.pages || 1,
		};
	} catch (error) {
		console.error('Error in listProducts:', error);
		throw error;
	}
}

export async function getProduct(id: string): Promise<Product> {
	try {
		// Use relative path if not on localhost (works for both mobile and server)
		let fullUrl: string;
		if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
			// Use absolute URL for localhost development
			fullUrl = `${API_BASE_URL}/api/products/${id}`;
		} else {
			// Use relative path for production/server/mobile
			fullUrl = `/api/products/${id}`;
		}
		
		const res = await fetch(fullUrl);
		if (!res.ok) {
			console.error(`API Error: ${res.status} ${res.statusText} for URL: ${fullUrl}`);
			throw new Error('Product not found');
		}
		const data = await res.json();
		return mapProduct(data);
	} catch (error) {
		console.error('Error in getProduct:', error);
		throw error;
	}
}

export async function getProductMedia(idOrSlug: string): Promise<{ images: string[]; banners: string[]; videos: string[] }> {
  try {
    // Use relative path if not on localhost (works for both mobile and server)
    let fullUrl: string;
    if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
      // Use absolute URL for localhost development
      fullUrl = `${API_BASE_URL}/api/products/${idOrSlug}/media`;
    } else {
      // Use relative path for production/server/mobile
      fullUrl = `/api/products/${idOrSlug}/media`;
    }
    
    const res = await fetch(fullUrl);
    if (!res.ok) {
      console.error(`API Error: ${res.status} ${res.statusText} for URL: ${fullUrl}`);
      throw new Error('Failed to fetch product media');
    }
    const data = await res.json();
    // Support both formats: { media: {...} } and { images: ..., banners: ..., videos: ... }
    if (data.media) {
      return { images: data.media.images || [], banners: data.media.banners || [], videos: data.media.videos || [] };
    }
    return { images: data.images || [], banners: data.banners || [], videos: data.videos || [] };
  } catch (error) {
    console.error('Error in getProductMedia:', error);
    throw error;
  }
}
